import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { getSaltBlock } from "../lib/api";
import NextPage from "../components/NextPage";
import SEO from "../components/SEO";
import ChapterHero from "../components/ChapterHero";
import { Butterfly } from "../components/Butterflies";
import PinchOfSalt from "../components/PinchOfSalt";

/**
 * Salt-spill animation
 * Six pink salt grains fall from the salt-block shaker. One glows, transforms
 * into a Saltie Wink™ butterfly, and gently flies toward the scripture card.
 * Plays once on mount — no loops. Respects prefers-reduced-motion.
 */
const SaltSpill = () => {
  const [reduce] = useState(
    typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  if (reduce) return null;

  // Salt grains stream from the actual perforated lid on the tipped shaker
  // (upper-left area of the salt block). The shaker leans ~30° to the right,
  // so grains follow that angle for the first stretch, then gravity dominates.
  // Origin is placed AT the lid holes — no longer at the shaker body.
  const grains = [
    { x: 14, delay: 0.10, spin: -80, fall: 210 },
    { x: 22, delay: 0.35, spin: 60, fall: 260 },
    { x: 8, delay: 0.55, spin: -140, fall: 230 },
    { x: 28, delay: 0.75, spin: 100, fall: 280 },
    { x: 18, delay: 0.95, spin: -60, fall: 220 },
  ];

  return (
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none z-20"
      data-testid="salt-spill"
    >
      {/* pink salt grains falling from the shaker */}
      {grains.map((g, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full"
          style={{
            top: "18%",
            left: "32%",
            width: 5,
            height: 5,
            background:
              "radial-gradient(circle at 30% 30%, #ffe4d6, #f5b8a3 60%, #d68c76)",
            boxShadow: "0 0 6px rgba(245,184,163,0.6)",
          }}
          initial={{ x: 0, y: 0, opacity: 0, rotate: 0 }}
          animate={{
            x: g.x,
            y: g.fall,
            opacity: [0, 1, 1, 0],
            rotate: g.spin,
          }}
          transition={{
            duration: 1.6,
            delay: g.delay,
            ease: "easeIn",
            times: [0, 0.15, 0.85, 1],
            repeat: Infinity,
            repeatDelay: 0,
          }}
        />
      ))}

      {/* The "chosen" grain that glows into a butterfly */}
      <motion.span
        className="absolute rounded-full"
        style={{
          top: "18%",
          left: "32%",
          width: 7,
          height: 7,
          background:
            "radial-gradient(circle at 30% 30%, #ffffff, #ffe1a8 55%, #e2a75c)",
          boxShadow: "0 0 14px rgba(255,220,150,0.9)",
        }}
        initial={{ x: 0, y: 0, opacity: 0, scale: 1 }}
        animate={{
          x: [0, 24, 48],
          y: [0, 130, 230],
          opacity: [0, 1, 1, 0],
          scale: [1, 1.6, 2.2, 0],
        }}
        transition={{
          duration: 1.8,
          delay: 1.4,
          ease: "easeInOut",
          times: [0, 0.35, 0.75, 1],
          repeat: Infinity,
          repeatDelay: 5.3,
        }}
      />

      {/* The butterfly that emerges and flies to the scripture card */}
      <motion.span
        className="absolute"
        style={{
          top: "18%",
          left: "32%",
        }}
        initial={{ x: 0, y: 0, opacity: 0, scale: 0.2, rotate: -20 }}
        animate={{
          x: [0, 40, 100, 160],
          y: [220, 260, 340, 430],
          opacity: [0, 1, 1, 1, 0],
          scale: [0.2, 1, 1, 1.05, 1],
          rotate: [-20, -6, 8, -4, 0],
        }}
        transition={{
          duration: 2.8,
          delay: 2.7,
          ease: "easeInOut",
          times: [0, 0.15, 0.55, 0.85, 1],
          repeat: Infinity,
          repeatDelay: 4.3,
        }}
      >
        <Butterfly />
      </motion.span>
    </div>
  );
};

/**
 * Faint pasture silhouette behind the scripture card — like old stationery.
 */
const PastureSilhouette = () => (
  <svg
    aria-hidden
    className="absolute inset-0 w-full h-full pointer-events-none"
    viewBox="0 0 800 300"
    preserveAspectRatio="xMidYMid slice"
    style={{ opacity: 0.07 }}
  >
    {/* rolling hills */}
    <path
      d="M0,230 C120,200 220,240 340,215 C460,190 560,235 700,210 C760,200 800,210 800,210 L800,300 L0,300 Z"
      fill="#4B3A2F"
    />
    <path
      d="M0,260 C160,240 300,275 440,255 C580,235 700,270 800,255 L800,300 L0,300 Z"
      fill="#2C2724"
    />
    {/* small barn */}
    <g transform="translate(560,190)" fill="#4B3A2F">
      <polygon points="0,20 26,6 52,20 52,50 0,50" />
      <polygon points="0,20 26,6 52,20 26,14" opacity="0.7" />
      <rect x="20" y="34" width="12" height="16" fill="#F7F0E4" opacity="0.4" />
    </g>
    {/* windmill */}
    <g transform="translate(640,175)" stroke="#4B3A2F" strokeWidth="1.4" fill="none">
      <line x1="0" y1="18" x2="0" y2="65" />
      <line x1="0" y1="18" x2="14" y2="4" />
      <line x1="0" y1="18" x2="-14" y2="4" />
      <line x1="0" y1="18" x2="14" y2="32" />
      <line x1="0" y1="18" x2="-14" y2="32" />
    </g>
    {/* split-rail fence */}
    <g stroke="#4B3A2F" strokeWidth="1.4" strokeLinecap="round">
      <line x1="30" y1="252" x2="360" y2="240" />
      <line x1="30" y1="266" x2="360" y2="254" />
      <line x1="60" y1="248" x2="60" y2="272" />
      <line x1="150" y1="245" x2="150" y2="270" />
      <line x1="240" y1="243" x2="240" y2="268" />
      <line x1="330" y1="241" x2="330" y2="266" />
    </g>
  </svg>
);

/**
 * Small journal-page divider — hearts, salt crystal, hearts.
 */
const Divider = () => (
  <div className="flex items-center justify-center gap-3 my-8" aria-hidden>
    <span className="text-[#A87149]/60 text-xs">• •</span>
    <span className="text-[#D62839]">♥</span>
    <svg width="14" height="14" viewBox="0 0 14 14" className="text-[#A87149]">
      <polygon
        points="7,1 12,4 12,10 7,13 2,10 2,4"
        fill="currentColor"
        opacity="0.85"
      />
    </svg>
    <span className="text-[#D62839]">♥</span>
    <span className="text-[#A87149]/60 text-xs">• •</span>
  </div>
);

/**
 * Pressed wildflower / wheat stem tucked in the corner of the scripture card.
 */
const PressedFlower = ({ className = "" }) => (
  <svg
    aria-hidden
    viewBox="0 0 60 80"
    className={className}
    style={{ opacity: 0.6 }}
  >
    <g stroke="#7E9268" strokeWidth="1.4" fill="none" strokeLinecap="round">
      <path d="M30,78 C30,60 26,45 32,28 C36,16 30,8 32,2" />
      <path d="M32,52 C24,50 20,44 16,40" />
      <path d="M32,42 C40,40 44,34 46,30" />
      <path d="M32,32 C26,30 22,26 20,22" />
    </g>
    {/* pink cosmos flower */}
    <g transform="translate(32,8)">
      {[0, 60, 120, 180, 240, 300].map((deg) => (
        <ellipse
          key={deg}
          cx="0"
          cy="-5"
          rx="3"
          ry="6"
          fill="#E9A6C1"
          transform={`rotate(${deg})`}
          opacity="0.75"
        />
      ))}
      <circle cx="0" cy="0" r="2.4" fill="#F2C64A" />
    </g>
    {/* small yellow bloom on side stem */}
    <circle cx="16" cy="40" r="3" fill="#F5D97A" opacity="0.75" />
    <circle cx="46" cy="30" r="3" fill="#C48BC7" opacity="0.7" />
  </svg>
);

export default function SaltBlock() {
  const [sb, setSb] = useState(null);

  useEffect(() => {
    getSaltBlock().then(setSb).catch(() => {});
  }, []);

  if (!sb) {
    return (
      <div className="parchment min-h-screen flex items-center justify-center">
        <SEO
          title="The Salt Block"
          description="A quiet monthly pause — scripture, reflection, and prayer for the pasture."
          path="/salt-block"
        />
        <p className="font-script text-2xl text-[#4B3A2F]">a quiet moment...</p>
      </div>
    );
  }

  return (
    <div className="parchment min-h-screen relative overflow-hidden" data-testid="salt-block-page">
      <SEO
        title="The Salt Block"
        description={`This month at the pasture: "${sb.scripture.slice(0, 110)}..." — ${sb.scripture_reference}. A monthly scripture, reflection, and prayer.`}
        path="/salt-block"
      />

      <ChapterHero
        path="/salt-block"
        image="/assets/heroes/salt-block.webp"
        title="The Salt Block"
        titleAlt="Chapter 14 – The Salt Block – A little salt can change everything"
        subtitle="A little salt&hellip; can change everything."
        position="top-left"
        titleGraphic={
          <div
            className="relative select-none"
            style={{
              width: "clamp(170px, 34vw, 560px)",
              // Give the salt block a soft ambient shadow so it reads
              // as a real object sitting on the sky rather than a
              // sticker pasted onto the illustration.
              filter: "drop-shadow(0 12px 24px rgba(60,40,20,0.35))",
              // Establish a container so the chapter text scales
              // proportionally with the salt block width (cqw units).
              containerType: "inline-size",
              containerName: "chapter-marker",
            }}
            data-testid="salt-block-chapter-marker"
          >
            <img
              src="/assets/salt-block-plain.webp"
              alt="Natural pink Himalayan salt block chapter marker"
              draggable="false"
              className="block w-full h-auto"
              style={{ maxWidth: "none" }}
            />
            {/* Chapter text engraved on the face of the salt block.
                Centered horizontally + vertically with comfortable
                margins on all four sides. All three lines use the
                Bakerie typeface with sizes scaled by container-query
                units so the marker feels beautifully proportioned to
                the block at every viewport. */}
            <div
              className="absolute inset-0 flex flex-col items-center justify-center text-center"
              style={{ padding: "11% 14%" }}
            >
              <p
                className="uppercase"
                style={{
                  fontFamily: "'Bakerie', 'Cormorant Garamond', serif",
                  fontWeight: 500,
                  color: "#2A1B10",
                  fontSize: "clamp(1rem, 4.6cqi, 2rem)",
                  letterSpacing: "0.36em",
                  lineHeight: 1.1,
                }}
                data-testid="chapter-marker-eyebrow"
              >
                Chapter 14
              </p>
              <h1
                className="leading-[0.98]"
                style={{
                  fontFamily: "'Bakerie', 'Cormorant Garamond', serif",
                  fontWeight: 700,
                  color: "#2A1B10",
                  fontSize: "clamp(2.4rem, 10cqi, 5.4rem)",
                  marginTop: "0.32em",
                }}
                data-testid="chapter-marker-title"
              >
                The Salt Block
              </h1>
              <p
                style={{
                  fontFamily: "'Bakerie', 'Cormorant Garamond', serif",
                  fontWeight: 400,
                  fontStyle: "italic",
                  color: "#4B3A2F",
                  fontSize: "clamp(1.1rem, 4.2cqi, 2.15rem)",
                  marginTop: "0.42em",
                  lineHeight: 1.2,
                }}
                data-testid="chapter-marker-subtitle"
              >
                A little salt&hellip; can change everything.
              </p>
            </div>
          </div>
        }
      />

      <div className="relative max-w-3xl mx-auto text-center px-6 -mt-8 pb-24 z-10">
        <Divider />

        <p className="font-script text-2xl text-[#4B3A2F]">{sb.month_label}</p>
        <div className="gold-rule my-8" />

        {/* Scripture — old journal page, torn edges, pressed wildflower corner */}
        <section
          data-testid="salt-block-scripture"
          className="relative mb-10"
        >
          {/* faint pasture silhouette behind card */}
          <div className="absolute inset-0 -mx-4 rounded-md overflow-hidden pointer-events-none">
            <PastureSilhouette />
          </div>

          <div
            className="relative p-10 md:p-12"
            style={{
              background:
                "linear-gradient(180deg, #FBF6EA 0%, #F5EEDD 100%)",
              borderRadius: "3px",
              boxShadow:
                "0 22px 34px -18px rgba(60,35,15,0.35), 0 2px 0 rgba(90,60,30,0.10), inset 0 0 60px rgba(160,110,60,0.06)",
              // torn / deckle edge using an SVG-based mask
              WebkitMaskImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='none'><rect x='0' y='0' width='100' height='100' fill='white' /><path d='M0,0 L100,0 L100,2 Q95,3 90,1 T80,2 T70,1 T60,3 T50,1 T40,2 T30,1 T20,3 T10,1 L0,2 Z' fill='%23F7F0E4'/><path d='M0,100 L100,100 L100,98 Q95,97 90,99 T80,98 T70,99 T60,97 T50,99 T40,98 T30,99 T20,97 T10,99 L0,98 Z' fill='%23F7F0E4' /></svg>\")",
              WebkitMaskSize: "100% 100%",
              maskImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='none'><rect x='0' y='0' width='100' height='100' fill='white' /></svg>\")",
            }}
          >
            {/* pressed wildflower — top-right corner */}
            <PressedFlower className="absolute top-2 right-3 w-10 h-14" />
            {/* small wheat stem — bottom-left corner */}
            <PressedFlower className="absolute bottom-2 left-3 w-8 h-12 rotate-[190deg] opacity-70" />

            <p className="text-xs uppercase tracking-[0.32em] text-[#7E9268] mb-5 flex items-center justify-center gap-2">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path d="M6,1 L6,11 M3,3 L6,6 M9,3 L6,6 M3,7 L6,10 M9,7 L6,10"
                  stroke="#7E9268" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
              <span>A Pasture Thought</span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path d="M6,1 L6,11 M3,3 L6,6 M9,3 L6,6 M3,7 L6,10 M9,7 L6,10"
                  stroke="#7E9268" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            </p>
            <blockquote className="font-display italic text-2xl md:text-[1.75rem] text-[#2C2724] leading-[1.55]">
              &ldquo;{sb.scripture}&rdquo;
            </blockquote>
            <p className="mt-5 text-sm uppercase tracking-[0.25em] text-[#4B3A2F]">
              {sb.scripture_reference}
            </p>
          </div>
        </section>

        {/* Go be the salt */}
        <section data-testid="salt-block-cta" className="p-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#4B3A2F] mb-3">
            Go Be the Salt
          </p>
          <p className="font-display text-2xl md:text-3xl text-[#4B3A2F] leading-relaxed max-w-xl mx-auto">
            {sb.go_be_the_salt}
          </p>
        </section>
      </div>

      {/* Bridge tagline — the quiet transition into 365 Grains of Salt™.
          Larger typography with a slow, warm-evening sunset glow behind
          the lettering so it reads as a welcoming focal point. The glow
          animates gently on a long cycle and respects reduced-motion. */}
      <div className="max-w-3xl mx-auto text-center px-6 pt-8 pb-14 sm:pt-12 sm:pb-20 z-10 relative">
        <div className="relative inline-block mx-auto">
          {/* Warm sunset glow, softly breathing behind the words. */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none tagline-glow"
            style={{
              background:
                "radial-gradient(ellipse at 50% 55%, rgba(255,196,120,0.55) 0%, rgba(255,183,90,0.32) 35%, rgba(255,183,90,0) 72%)",
              filter: "blur(28px)",
              transform: "scale(1.35)",
              zIndex: 0,
            }}
          />
          <p
            className="font-display italic text-[#4B3A2F] relative"
            style={{
              fontSize: "clamp(1.55rem, 3.2vw, 2.55rem)",
              lineHeight: 1.5,
              letterSpacing: "0.005em",
              zIndex: 1,
            }}
            data-testid="salt-block-bridge-tagline"
          >
            The salt block nourishes the herd.
            <br />
            <span className="text-[#7E9268]">God&rsquo;s Word nourishes our hearts.</span>
          </p>
        </div>
        <div className="gold-rule mx-auto mt-8" style={{ width: "5rem" }} />
      </div>

      {/* 365 Grains of Salt™ — A Pinch of Salt™ interactive section. Sits
          beneath the bridge tagline as a seamless continuation of the same
          storybook page (no new chapter, no navigation change). */}
      <PinchOfSalt />

      <NextPage to="/story-barn" label="Visit the Story Barn" />
    </div>
  );
}
